// AI News Feed – Backend-connected with hardened HTTPS fallback
// - avoids insecure localhost fallback in production
// - tolerates unavailable backend by returning curated fallback items
// - preserves export compatibility
//
// © 2026 Advanced Advisory Services, LLC. All Rights Reserved.

const DEFAULT_PROD_BACKEND = "https://ai-fine-protector-api.onrender.com";

function cleanBase(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  return raw.replace(/\/$/, "");
}

function resolveBackendBase() {
  const candidates = [
    process.env.REACT_APP_BACKEND_URL,
    process.env.REACT_APP_API_BASE,
  ].map(cleanBase).filter(Boolean);

  if (candidates.length) return candidates[0];

  if (typeof window !== "undefined") {
    const host = String(window.location.hostname || "").toLowerCase();
    const isLocal = host === "localhost" || host === "127.0.0.1" || host.endsWith('.local');
    if (isLocal) {
      // Local frontend often runs without a local backend. Default to the stable hosted API.
      return DEFAULT_PROD_BACKEND;
    }
  }

  return DEFAULT_PROD_BACKEND;
}

const BACKEND_URL = resolveBackendBase();

export const fallbackNews = [
  {
    id: "fallback-us-ftc-ai-bias",
    title: "FTC signals increased enforcement on AI bias and discrimination",
    jurisdiction: "US",
    category: "Bias / Discrimination",
    severity: "High",
    summary:
      "The Federal Trade Commission has indicated a stronger enforcement posture toward AI systems that produce discriminatory outcomes, particularly in hiring and lending contexts.",
    source: "https://www.ftc.gov/",
    date: "2026-03-01",
  },
  {
    id: "fallback-eu-ai-act",
    title: "EU AI Act enforcement phase begins with stricter compliance expectations",
    jurisdiction: "EU",
    category: "Regulatory Framework",
    severity: "High",
    summary:
      "Regulators are beginning early enforcement activity under the EU AI Act, focusing on high-risk systems and transparency obligations.",
    source: "https://artificialintelligenceact.eu/",
    date: "2026-02-20",
  },
  {
    id: "fallback-uk-ai-governance",
    title: "UK updates AI governance guidance for public and private sector",
    jurisdiction: "UK",
    category: "Governance",
    severity: "Medium",
    summary:
      "The UK government has released updated AI governance guidance emphasizing accountability, documentation, and risk controls.",
    source: "https://www.gov.uk/",
    date: "2026-02-10",
  },
];

function normalizeJurisdiction(value) {
  const raw = String(value || "").trim().toLowerCase();

  if (
    raw.includes("united states") ||
    raw === "us" ||
    raw === "u.s." ||
    raw === "u.s"
  ) {
    return "US";
  }

  if (raw.includes("european union") || raw === "eu") {
    return "EU";
  }

  if (raw.includes("united kingdom") || raw === "uk") {
    return "UK";
  }

  if (!raw) return "Global";
  return String(value || "Global");
}

function normalizeCategory(value) {
  const raw = String(value || "").trim();
  return raw || "Regulatory Signal";
}

function normalizeSeverity(value) {
  const raw = String(value || "").trim();
  if (!raw) return "Medium";
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
}

function normalizeSource(value) {
  const raw = String(value || "").trim();
  return raw || "#";
}

function normalizeDate(value) {
  const raw = String(value || "").trim();
  if (!raw) return new Date().toISOString().split("T")[0];
  return raw;
}

function normalizeItem(item) {
  return {
    id: item?.id || Math.random().toString(36).slice(2),
    title: item?.title || "Untitled",
    jurisdiction: normalizeJurisdiction(item?.jurisdiction),
    category: normalizeCategory(item?.category),
    severity: normalizeSeverity(item?.severity),
    summary: item?.summary || "",
    source: normalizeSource(item?.source || item?.source_url),
    source_url: normalizeSource(item?.source_url || item?.source),
    source_name: String(item?.source_name || "").trim(),
    scope: String(item?.scope || "").trim().toUpperCase(),
    state_code: String(item?.state_code || "").trim().toUpperCase(),
    date: normalizeDate(item?.date),
  };
}

function appendFilter(params, key, value) {
  const raw = String(value || "").trim();
  if (!raw || raw.toLowerCase() === "all") return;
  params.set(key, raw);
}

export async function fetchAINews(filters = {}) {
  try {
    const params = new URLSearchParams();
    appendFilter(params, "jurisdiction", filters?.jurisdiction);
    appendFilter(params, "severity", filters?.severity);
    appendFilter(params, "category", filters?.category);
    if (!params.has("limit")) params.set("limit", String(filters?.limit || 100));

    const qs = params.toString();
    const url = `${BACKEND_URL}/api/ai-news${qs ? `?${qs}` : ""}`;

    const res = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    });

    if (!res.ok) {
      throw new Error(`Backend response not OK (${res.status})`);
    }

    const data = await res.json();
    const items = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : null;
    if (!items) {
      throw new Error("Invalid backend response shape");
    }

    return items.map(normalizeItem);
  } catch (err) {
    console.warn("AI News fallback triggered:", err);
    return fallbackNews.map(normalizeItem);
  }
}

export default fallbackNews;
